import { getCurrentDateTime } from '../../utils/dateUtils';
import { kvGetRaw, kvSetRaw } from '../../infrastructure/storage/kvAccess';
import { sumRevenue } from '../finance/revenue';
import { resolveSupabaseBusinessId } from '../../utils/businessTypeCloud';

export function buildReportPayload(businessId, currentData) {
  const products = currentData?.products || [];
  const sales = currentData?.sales || [];

  return {
    business_id: businessId,
    generated_at: getCurrentDateTime(),
    totalProducts: products.length,
    totalSales: sales.length,
    totalRevenue: sumRevenue(sales),
    products: products.slice(0, 100),
    sales: sales.slice(-50),
    purchases: (currentData?.purchases || []).slice(-50),
    orders: (currentData?.orders || []).slice(-50),
    customers: currentData?.customers || [],
    suppliers: currentData?.suppliers || [],
    expiredProducts: currentData?.expiredProducts || []
  };
}

export function isReportSyncDisabled(businessId) {
  return Boolean(kvGetRaw(`sync_disabled_${businessId}`));
}

export function disableReportSyncForBusiness(businessId) {
  kvSetRaw(`sync_disabled_${businessId}`, 'true');
}

export function hasReportDataChanged(businessId, currentData) {
  const lastSyncKey = `last_sync_${businessId}`;
  const lastSyncData = kvGetRaw(lastSyncKey);
  const currentDataString = JSON.stringify(currentData);
  return lastSyncData !== currentDataString;
}

export function markReportDataSynced(businessId, currentData) {
  kvSetRaw(`last_sync_${businessId}`, JSON.stringify(currentData));
}

/**
 * Sync reports to Supabase or outbox — no React state, no navigator.onLine gate.
 */
export async function executeReportSync({
  businessId,
  currentData,
  reportsService,
  offlineService
}) {
  if (!businessId || !currentData) {
    return { skipped: true, reason: 'missing-input' };
  }

  if (isReportSyncDisabled(businessId)) {
    return { skipped: true, reason: 'disabled' };
  }

  if (!hasReportDataChanged(businessId, currentData)) {
    return { skipped: true, reason: 'unchanged' };
  }

  const reportData = buildReportPayload(businessId, currentData);
  const remoteBusinessId = resolveSupabaseBusinessId(businessId) || businessId;

  try {
    const result = await reportsService.syncReports(remoteBusinessId, reportData);

    if (result?.success) {
      markReportDataSynced(businessId, currentData);
      return { success: true, result };
    }

    if (result?.code === 'SCHEMA_ERROR') {
      disableReportSyncForBusiness(businessId);
      return { success: false, result };
    }

    offlineService.queueReport(businessId, reportData);
    return { success: true, queued: true, result };
  } catch (error) {
    offlineService.queueReport(businessId, reportData);
    return { success: true, queued: true, error: error?.message || String(error) };
  }
}

export async function executeLegacyReportSync({
  businessId,
  products,
  sales,
  purchases,
  orders,
  customers,
  suppliers,
  expiredProducts,
  reportsService
}) {
  if (!businessId) {
    return { skipped: true };
  }

  const reportData = buildReportPayload(businessId, {
    products,
    sales,
    purchases,
    orders,
    customers,
    suppliers,
    expiredProducts
  });
  const remoteBusinessId = resolveSupabaseBusinessId(businessId) || businessId;

  try {
    await reportsService.syncReports(remoteBusinessId, reportData);
    return { success: true };
  } catch (error) {
    return { success: false, error: error?.message || String(error) };
  }
}
